import { apiFetch } from "@/lib/api";
import type {
  PodcastSearchResult,
  PodcastFeedResponse,
  Subscription,
  LatestEpisode,
} from "@/types/podcast";

// ── Simple In-Memory Cache ────────────────────

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

const cache = new Map<string, CacheEntry<unknown>>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

function getCached<T>(key: string): T | null {
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.timestamp > CACHE_TTL) {
    cache.delete(key);
    return null;
  }
  return entry.data as T;
}

function setCache<T>(key: string, data: T): void {
  cache.set(key, { data, timestamp: Date.now() });
}

// ── Prefetch (for hover) ──────────────────────

const prefetchingUrls = new Set<string>();

export function prefetchPodcastFeed(feedUrl: string): void {
  const cacheKey = `feed:${feedUrl}:20:0`;
  // Already cached or currently fetching
  if (getCached(cacheKey) || prefetchingUrls.has(feedUrl)) return;
  
  prefetchingUrls.add(feedUrl);
  getPodcastFeed(feedUrl, 20, 0)
    .finally(() => prefetchingUrls.delete(feedUrl));
}

// ── Public (no auth required) ─────────────────

export async function getLatestEpisodes(
  limit = 10,
): Promise<LatestEpisode[]> {
  const cacheKey = `latest:${limit}`;
  const cached = getCached<LatestEpisode[]>(cacheKey);
  if (cached) return cached;

  try {
    const data = await apiFetch<{ episodes: LatestEpisode[] }>(
      `/podcasts/latest-episodes?limit=${limit}`,
    );
    setCache(cacheKey, data.episodes);
    return data.episodes;
  } catch {
    return [];
  }
}

export async function getPopularPodcasts(): Promise<PodcastSearchResult[]> {
  const cacheKey = "popular";
  const cached = getCached<PodcastSearchResult[]>(cacheKey);
  if (cached) return cached;

  try {
    const data = await apiFetch<{ podcasts: PodcastSearchResult[] }>(
      "/podcasts/popular",
    );
    setCache(cacheKey, data.podcasts);
    return data.podcasts;
  } catch {
    return [];
  }
}

export async function searchPodcasts(
  query: string,
  limit = 10,
): Promise<PodcastSearchResult[]> {
  const data = await apiFetch<{ results: PodcastSearchResult[] }>(
    `/podcasts/search?q=${encodeURIComponent(query)}&limit=${limit}`,
  );
  return data.results;
}

export async function getPodcastFeed(
  feedUrl: string,
  limit = 20,
  offset = 0,
  search?: string,
): Promise<PodcastFeedResponse> {
  const cacheKey = `feed:${feedUrl}:${limit}:${offset}:${search || ''}`;
  const cached = getCached<PodcastFeedResponse>(cacheKey);
  if (cached) return cached;

  let url = `/podcasts/feed?url=${encodeURIComponent(feedUrl)}&limit=${limit}&offset=${offset}`;
  if (search) {
    url += `&search=${encodeURIComponent(search)}`;
  }

  const data = await apiFetch<PodcastFeedResponse>(url);
  setCache(cacheKey, data);
  return data;
}

// ── Auth required ─────────────────────────────

export async function subscribePodcast(
  feedUrl: string,
  podcastName: string,
  artworkUrl: string,
  author: string,
): Promise<{ status: string }> {
  return apiFetch<{ status: string }>("/podcasts/subscribe", {
    method: "POST",
    body: JSON.stringify({
      feed_url: feedUrl,
      podcast_name: podcastName,
      artwork_url: artworkUrl,
      author,
    }),
  });
}

export async function unsubscribePodcast(
  feedUrl: string,
): Promise<{ status: string }> {
  return apiFetch<{ status: string }>("/podcasts/unsubscribe", {
    method: "POST",
    body: JSON.stringify({ feed_url: feedUrl }),
  });
}

export async function getSubscriptions(): Promise<Subscription[]> {
  const data = await apiFetch<{ subscriptions: Subscription[] }>(
    "/podcasts/subscriptions",
  );
  return data.subscriptions;
}

export async function checkTranscribed(
  audioUrls: string[],
): Promise<Record<string, number>> {
  try {
    const data = await apiFetch<{ transcribed: Record<string, number> }>(
      "/podcasts/check-transcribed",
      {
        method: "POST",
        body: JSON.stringify({ audio_urls: audioUrls }),
      },
    );
    return data.transcribed;
  } catch {
    return {};
  }
}

export interface TranscribeResponse {
  job_id: string;
  status: "processing" | "completed" | "failed";
  cached?: boolean;
  result?: {
    saved_item_id?: string;
    title?: string;
  };
}

export async function transcribeEpisode(
  audioUrl: string,
  metadata: { title: string; podcast_name: string; description: string; artwork_url?: string; feed_url?: string },
  language: string = "auto",
): Promise<TranscribeResponse> {
  return apiFetch<TranscribeResponse>("/transcribe", {
    method: "POST",
    body: JSON.stringify({
      url: audioUrl,
      source: "rss",
      metadata,
      language,
    }),
  });
}
